import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';   // Import for Firebase Authentication
import 'firebase/compat/firestore';  // Import for Firestore
import 'firebase/compat/storage';  // Import for Storage

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: 'AIzaSyAB10QoTXFcdUCaVSbZntf_d_cSj7OSjHM',
  authDomain: 'gottea-3daa4.firebaseapp.com',
  databaseURL: 'https://gottea-3daa4.firebaseio.com',
  projectId: 'gottea-3daa4',
  storageBucket: 'gottea-3daa4.appspot.com',
  messagingSenderId: '691417546594',
  appId: '1:691417546594:web:536ef6d0c379fb5947b63b',
  measurementId: 'G-ED1W31MB1J',
};

// Initialize Firebase
firebase.initializeApp(firebaseConfig);

// Export Auth, Firestore, and Storage
const auth = firebase.auth();        // Initialize Firebase Auth
const rawFirestore = firebase.firestore();  // Used only by non-admin legacy integrations.
const storage = firebase.storage();   // Initialize Storage

// Initialize Google Auth Provider
const googleProvider = new firebase.auth.GoogleAuthProvider();

const API_BASE_URL = process.env.REACT_APP_GOTTEA_API_BASE_URL || 'https://gottea-search-5gtrn.ondigitalocean.app';
const RESOURCE_NAMES = {
  menuItems: 'menuItems',
  cafeTVs: 'devices',
  branches: 'branches',
  schedules: 'schedules',
  menuCategoryIcons: 'categoryIcons',
};
const listeners = new Map();

class AdminDocumentSnapshot {
  constructor(item) { this.id = item?.id; this.item = item || null; this.exists = Boolean(item); }
  data() {
    if (!this.item) return undefined;
    const { id, ...data } = this.item;
    return data;
  }
  get(field) { return this.item?.[field]; }
}

class AdminQuerySnapshot {
  constructor(items) { this.docs = items.map((item) => new AdminDocumentSnapshot(item)); this.size = this.docs.length; this.empty = this.size === 0; }
  forEach(callback) { this.docs.forEach(callback); }
}

async function resourceRequest(resource, suffix = '', options = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in.');
  const token = await user.getIdToken();
  const sessionId = window.localStorage.getItem('gottea_admin_session_id');
  const response = await fetch(`${API_BASE_URL}/admin/resources/${resource}${suffix}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(sessionId ? { 'X-Admin-Session-Id': sessionId } : {}),
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.success === false) {
    const error = new Error(body.error || `Request failed with status ${response.status}`);
    error.code = body.code;
    error.data = body.data;
    throw error;
  }
  return body.data || body;
}

async function loadCollection(resource) {
  const data = await resourceRequest(resource);
  return data.items || [];
}

async function notifyCollection(resource) {
  const callbacks = listeners.get(resource);
  if (!callbacks?.size) return;
  try {
    const snapshot = new AdminQuerySnapshot(await loadCollection(resource));
    callbacks.forEach(({ next }) => next(snapshot));
  } catch (error) {
    callbacks.forEach(({ error: onError }) => onError?.(error));
  }
}

class AdminDocumentReference {
  constructor(resource, id) { this.resource = resource; this.id = id; }
  async get() {
    try { const data = await resourceRequest(this.resource, `/${encodeURIComponent(this.id)}`); return new AdminDocumentSnapshot(data.item); }
    catch (error) { if (error.code === 'resource_not_found') return new AdminDocumentSnapshot(null); throw error; }
  }
  async set(data) { const result = await resourceRequest(this.resource, `/${encodeURIComponent(this.id)}`, { method: 'PUT', body: JSON.stringify(data) }); await notifyCollection(this.resource); return result; }
  async update(data) { return this.set(data); }
  async delete() { const result = await resourceRequest(this.resource, `/${encodeURIComponent(this.id)}`, { method: 'DELETE' }); await notifyCollection(this.resource); return result; }
  onSnapshot(next, error) {
    let active = true;
    this.get().then((snapshot) => { if (active) next(snapshot); }).catch((reason) => error?.(reason));
    return () => { active = false; };
  }
}

class AdminCollectionReference {
  constructor(resource, filters = [], sort = null) { this.resource = resource; this.filters = filters; this.sort = sort; }
  doc(id) { return new AdminDocumentReference(this.resource, id); }
  where(field, operator, value) { return new AdminCollectionReference(this.resource, [...this.filters, { field, operator, value }], this.sort); }
  orderBy(field, direction = 'asc') { return new AdminCollectionReference(this.resource, this.filters, { field, direction }); }
  async get() {
    let items = await loadCollection(this.resource);
    this.filters.forEach(({ field, operator, value }) => {
      if (operator === '==') items = items.filter((item) => item[field] === value);
    });
    if (this.sort) items.sort((a, b) => String(a[this.sort.field] || '').localeCompare(String(b[this.sort.field] || '')) * (this.sort.direction === 'desc' ? -1 : 1));
    return new AdminQuerySnapshot(items);
  }
  async add(data) { const result = await resourceRequest(this.resource, '', { method: 'POST', body: JSON.stringify(data) }); await notifyCollection(this.resource); return new AdminDocumentReference(this.resource, result.item.id); }
  onSnapshot(next, error) {
    const entry = { next: async () => next(await this.get()), error };
    if (!listeners.has(this.resource)) listeners.set(this.resource, new Set());
    listeners.get(this.resource).add(entry);
    this.get().then(next).catch((reason) => error?.(reason));
    const timer = window.setInterval(() => this.get().then(next).catch((reason) => error?.(reason)), 30000);
    return () => { window.clearInterval(timer); listeners.get(this.resource)?.delete(entry); };
  }
}

const firestore = {
  collection(name) {
    const resource = RESOURCE_NAMES[name];
    if (!resource) throw new Error(`Admin Firestore access is not available for collection: ${name}`);
    return new AdminCollectionReference(resource);
  },
};

export { auth, firestore, rawFirestore, storage, googleProvider };
export default firebase;
